import React from 'react'
import { Badge } from 'primereact/badge'
import { useNavigate } from 'react-router-dom'
import { OlangItem } from '../Olang/user-interface/OlangItem/OlangItem'
import ButtonComponent from '../ButtonComponent/ButtonComponent'

/**
 * Composant PageHeader réutilisable
 * Affiche un grand badge avec:
 * - Bouton retour à gauche (style cohérent avec FamilleDetail)
 * - Titre de la page au milieu
 * - Information optionnelle à droite
 * 
 * @param {string} title - Titre de la page (optionnel si titleOlang est fourni)
 * @param {string} titleOlang - Clé OLang pour le titre (optionnel si title est fourni)
 * @param {function} onBack - Callback personnalisé pour le retour (optionnel)
 * @param {string} backPath - Chemin de navigation pour le retour (optionnel)
 * @param {boolean} showBackButton - Afficher/masquer le bouton retour (défaut: true)
 * @param {string|ReactNode} info - Information à afficher à droite (optionnel)
 * @param {string} infoIcon - Icône pour l'info (optionnel, utilisé seulement si info est string)
 * @param {number|string} infoBadgeValue - Valeur du badge (optionnel)
 * @param {string} infoBadgeSeverity - Couleur du badge (défaut: 'info')
 * @param {string} className - Classes CSS additionnelles (optionnel)
 */
export const PageHeader = ({ 
  title, 
  titleOlang, 
  onBack, 
  backPath,
  showBackButton = true,
  info,
  infoIcon,
  infoBadgeValue,
  infoBadgeSeverity = 'info',
  className = ''
}) => {
  const navigate = useNavigate()

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else if (backPath) {
      navigate(backPath)
    } else {
      navigate(-1)
    }
  }

  return (
    <div className={`surface-card shadow-2 border-round p-4 mb-4 ${className}`}>
      <div className="flex align-items-center justify-content-between">
        {/* Bouton retour à gauche - Style cohérent avec FamilleDetail */}
        <div className="flex align-items-center gap-3" style={{ minWidth: '150px' }}>
          {showBackButton && (
            <ButtonComponent onClick={handleBack}>
              <i className='fa-solid fa-share fa-flip-horizontal text-white'></i>
              <div className='ml-2 text-base font-semibold'>
                <OlangItem olang='btn.back' />
              </div>
            </ButtonComponent>
          )}
        </div>

        {/* Titre au milieu */}
        <div className="flex-1 text-center">
          <h1 className="text-3xl font-bold text-primary m-0">
            {titleOlang ? <OlangItem olang={titleOlang} /> : title}
          </h1>
        </div>

        {/* Information à droite */}
        <div className="flex align-items-center justify-content-end gap-3" style={{ minWidth: '150px' }}>
          {info && (
            // Si info est un élément React, le rendre directement, sinon l'envelopper
            typeof info === 'string' || typeof info === 'number' ? (
              <div className="flex align-items-center gap-2">
                {infoIcon && <i className={`${infoIcon} text-xl`}></i>}
                <span className="text-lg font-semibold">{info}</span>
                {infoBadgeValue && (
                  <Badge value={infoBadgeValue} severity={infoBadgeSeverity} size="large" />
                )}
              </div>
            ) : (
              // Rendre le JSX personnalisé directement
              info
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default PageHeader
